/**
 * EtaStrip — elapsed time, build progress and the estimate slot (leg `gui-eta`). Model: `etaModel.ts`.
 * The wrapper ticks every `TICK_MS` (15 s: durations show minutes, so a faster tick is wasted renders), only while the
 * task is live and the tab visible; the interval is cleared on unmount.
 */
import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TaskSnapshot } from "@/services/multi-agent/laneV2/types";
import { deriveEta, formatDuration, type EtaSlot } from "./etaModel";
import { isTerminalState } from "./views";

const TICK_MS = 15_000;

export function etaSlotText(slot: EtaSlot): string | null {
  switch (slot.kind) {
    case "none":
      return null;
    case "placeholder":
      return "Estimate available once the build starts.";
    case "gathering":
      return "Estimating: a few more items need to finish first.";
    case "estimate":
      return slot.capped
        ? "More than 3 hours left at the current pace (approximate, build only; the org check after it is not included)."
        : `About ${slot.minutesLeft} min left at the current pace (approximate, build only; the org check after it is not included).`;
    case "finishing":
      return "Build nearly finished.";
    case "paused":
      return slot.why === "parked" ? "Build paused; no estimate until it resumes." : "Waiting for you; no estimate until work resumes.";
    case "stalled":
      return `No recent progress (nothing finished for ${formatDuration(slot.sinceMs)}); no estimate right now.`;
    case "unknown_start":
      return "No estimate for this build.";
    case "repair":
      return `Repair round: ${slot.toRebuild} to rebuild.`;
    case "done":
      return slot.durationMs === null ? "Build finished." : `Build took ${formatDuration(slot.durationMs)}.`;
  }
}

export function EtaStripView({ snapshot, nowMs, className }: { snapshot: TaskSnapshot; nowMs: number; className?: string }) {
  const eta = deriveEta(snapshot, nowMs);
  const text = etaSlotText(eta.slot);
  const p = eta.progress;
  return (
    <div data-eta={eta.slot.kind} className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-muted/20 px-3.5 py-2 text-[12px] text-muted-foreground", className)}>
      <Clock className="h-3.5 w-3.5" aria-hidden />
      {eta.elapsedMs !== null && <span data-eta-elapsed>Elapsed {formatDuration(eta.elapsedMs)}</span>}
      {p && (
        <span data-eta-progress>
          {p.finished} of {p.total} items finished building{p.problem > 0 ? ` · ${p.problem} failed or blocked` : ""}{p.level ? ` · level ${p.level.k} of ${p.level.of}` : ""}
        </span>
      )}
      {text && <span data-eta-slot>{text}</span>}
    </div>
  );
}

export function EtaStrip({ snapshot, className }: { snapshot: TaskSnapshot; className?: string }) {
  const live = !isTerminalState(snapshot.state);
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const tick = () => {
      if (typeof document === "undefined" || !document.hidden) setNowMs(Date.now());
    };
    tick();
    const id = setInterval(tick, TICK_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [live]);
  return <EtaStripView snapshot={snapshot} nowMs={nowMs} className={className} />;
}
